"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { logoSmall } from "@/utils/image";

const TermsAndConditions = () => {
  const [domain, setDomain] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setDomain(window.location.origin);
    }
  }, []);

  return (
    <div className="py-5">
      <Link href="/login" className="d-flex justify-content-center mb-4">
        <Image src={logoSmall} alt="logo" />
      </Link>
      <div className="dashOuter">
        <h4 className="fs20 fw600 mb-4 text-center">Terms & Conditions</h4>
        <div className="text-start">
          <p>Effective Date: February 1, 2026</p>
          <p>Last Updated: February 7, 2026</p>
          <p>
            Terms URL:{" "}
            <Link href="/terms-condition" className="text-primary">
              {domain}/terms-condition
            </Link>
          </p>
          <p>
            Privacy Policy URL:{" "}
            <Link href="/privacy-policy" className="text-primary">
              {domain}/privacy-policy
            </Link>
          </p>

          <p className="mt-4">
            These Terms of Service (“Terms”) govern your access to and use of
            Hivora Circle (the “Service”), including our mobile applications,
            websites, and related services (collectively, “Hivora,” “we,”
            “us,” or “our”). By creating an account, accessing, or using
            Hivora Circle, you agree to these Terms.
          </p>

          <p className="fw-bold">
            If you do not agree to these Terms, do not use the Service.
          </p>

          <h5 className="mt-4 fw-bold">
            1) Eligibility and Basic Requirements
          </h5>
          <p>By using Hivora Circle, you represent and warrant that:</p>
          <ul>
            <li>
              You are at least 18 years old and legally able to enter into this
              agreement.
            </li>
            <li>
              You will use Hivora Circle only for lawful purposes and in
              compliance with these Terms and all applicable laws.
            </li>
            <li>
              The information you provide to Hivora Circle is accurate and you
              will keep it current.
            </li>
            <li>
              You are using Hivora Circle for personal, non-commercial purposes
              (no solicitation, advertising, or recruiting for commercial gain).
            </li>
            <li>
              You will not use Hivora Circle to exploit, harm, or attempt to harm
              other users.
            </li>
          </ul>
          <p>
            Hivora Circle may refuse access, suspend, or terminate accounts at its
            discretion for violations of these Terms or for safety, trust, and
            integrity reasons.
          </p>

          <h5 className="mt-4 fw-bold">2) Description of Hivora Circle</h5>
          <p>
            Hivora Circle is a peer-validated matchmaking platform designed to
            help users connect through a trusted “Circle of Trust.” Users may
            invite trusted individuals (“Circle Members”) to review and approve
            potential matches or interactions depending on features available in
            the Service.
          </p>
          <p>
            Hivora Circle does not guarantee matches, compatibility outcomes,
            relationship success, or user behavior.
          </p>

          <h5 className="mt-4 fw-bold">3) Account Registration and Security</h5>
          <p>You are responsible for:</p>
          <ul>
            <li>
              Maintaining the confidentiality of your login credentials and
              device access
            </li>
            <li>All activity that occurs under your account</li>
            <li>
              Notifying us immediately if you suspect unauthorized access or
              security issues
            </li>
          </ul>
          <p>
            We may use verification steps (including SMS/email verification) to
            protect accounts and reduce fraud.
          </p>

          <h5 className="mt-4 fw-bold">
            4) SMS/Text Messaging Program Terms (Required Disclosures)
          </h5>
          <p>
            <strong>Program Name:</strong> Hivora Circle Alerts & Verification
          </p>
          <p>
            <strong>Program Description:</strong> Hivora Circle uses SMS text
            messages to support account access and safety features, including:
          </p>
          <ul>
            <li>One-time passcodes and account verification</li>
            <li>Security alerts (e.g., suspicious login notifications)</li>
            <li>
              Circle invitations and Circle activity updates (where enabled)
            </li>
          </ul>
          <p>
            <strong>Message Frequency:</strong> Message frequency varies by user
            activity and settings. Typical use may include:
          </p>
          <ul>
            <li>Verification messages during login and account setup</li>
            <li>Occasional security alerts</li>
            <li>
              Invitation messages when you invite Circle Members or when Circle
              actions occur
            </li>
          </ul>
          <p>
            <strong>Message & Data Rates:</strong> Message and data rates may
            apply based on your mobile carrier plan.
          </p>
          <p>
            <strong>Opt-Out Instructions:</strong> To stop receiving SMS
            messages, reply STOP. For help, reply HELP.
          </p>
          <p>
            <strong>Support Contact:</strong>
            <br />
            Email: support@hivora.ai
            <br />
            Website:{" "}
            <Link href="/support" className="text-primary">
              {domain}/support
            </Link>
          </p>
          <p>
            By providing your phone number and completing any required opt-in
            steps, you consent to receive SMS messages from Hivora Circle for the
            purposes described above. Consent is not a condition of purchase.
          </p>

          <h5 className="mt-4 fw-bold">5) In-App Messaging Terms</h5>
          <p>
            Hivora Circle includes in-app messaging so users can communicate
            within the Service. Your use of in-app messaging must comply with
            these Terms and our community standards.
          </p>
          <h6>A) Messaging Rules (What You May Not Do)</h6>
          <p>You may not use in-app messaging to:</p>
          <ul>
            <li>Harass, threaten, stalk, intimidate, or shame another user</li>
            <li>
              Send hate speech, discriminatory language, or sexually explicit
              content
            </li>
            <li>
              Solicit money, gifts, payment information, or attempt scams/fraud
            </li>
            <li>Send spam, mass messages, or automated messages</li>
            <li>
              Share another person’s private information without permission
              (doxxing)
            </li>
            <li>Encourage illegal activity, violence, or self-harm</li>
            <li>Impersonate someone else or misrepresent your identity</li>
            <li>
              Send malware, links designed to steal information, or other
              harmful content
            </li>
          </ul>
          <h6>B) Safety and User Responsibility</h6>
          <p>
            You are solely responsible for your interactions with other users,
            including messages and any meetings that occur offline. Use caution,
            meet in public, and trust your instincts.
          </p>
          <h6>C) Blocking and Reporting</h6>
          <p>
            Hivora Circle provides features to block and report users. If you
            receive abusive, suspicious, or inappropriate messages, we encourage
            you to report them immediately through the app or by emailing
            support@hivora.ai.
          </p>
          <h6>D) Moderation and Enforcement</h6>
          <p>To maintain a safe community, Hivora Circle may:</p>
          <ul>
            <li>Review reported messages and associated account activity</li>
            <li>
              Remove content, restrict messaging, or suspend/terminate accounts
            </li>
            <li>Cooperate with law enforcement where required by law</li>
          </ul>
          <p>
            We do not guarantee that all harmful content will be caught before
            it is seen. Reporting helps us improve enforcement.
          </p>
          <h6>E) Message Availability and Retention</h6>
          <p>Messages may be stored and processed to:</p>
          <ul>
            <li>Deliver the messaging service</li>
            <li>Maintain platform security and prevent abuse</li>
            <li>Investigate reports and enforce these Terms</li>
            <li>Comply with legal obligations</li>
          </ul>
          <p>
            We retain message data only as long as reasonably necessary for
            these purposes, consistent with our Privacy Policy, and subject to
            applicable legal requirements.
          </p>

          <h5 className="mt-4 fw-bold">
            6) User Content and Community Standards
          </h5>
          <p>
            “User Content” includes any photos, profile information, messages,
            feedback, or other content you upload, post, send, or otherwise make
            available through Hivora Circle.
          </p>
          <p>
            You are responsible for your User Content and represent and warrant
            that:
          </p>
          <ul>
            <li>You have all necessary rights and permissions to post it</li>
            <li>It is accurate (to the best of your knowledge)</li>
            <li>It does not violate these Terms or applicable law</li>
          </ul>
          <p>
            We may remove content and restrict accounts that violate these
            standards.
          </p>

          <h5 className="mt-4 fw-bold">
            7) Circle Members and Shared Decisions
          </h5>
          <p>
            If you invite Circle Members, you confirm you have permission to
            share their contact details for the sole purpose of sending
            invitations and enabling Hivora Circle functionality.
          </p>
          <p>
            Circle Members’ approvals/declines and feedback reflect their
            personal views. Hivora Circle does not endorse any decision or
            guarantee safety or compatibility.
          </p>

          <h5 className="mt-4 fw-bold">
            8) Reporting, Safety, and Enforcement
          </h5>
          <p>
            We encourage users to report abuse, scams, or inappropriate
            behavior.
          </p>
          <p>Reporting: [Insert in-app path] or email support@hivora.ai</p>
          <p>
            We may investigate reports and take actions including warnings,
            content removal, limitations, suspension, or termination.
          </p>
          <p>
            Hivora Circle does not conduct comprehensive criminal background
            checks on all users and does not guarantee user identity beyond
            verification steps we may implement.
          </p>

          <h5 className="mt-4 fw-bold">9) License and Rights You Grant Us</h5>
          <p>You retain ownership of your User Content.</p>
          <p>
            By posting or submitting User Content through the Service, you grant
            Hivora Circle a worldwide, non-exclusive, royalty-free, transferable,
            sublicensable license to host, store, use, reproduce, display,
            modify (for formatting), and distribute your User Content solely to:
          </p>
          <ul>
            <li>Operate and provide the Service</li>
            <li>
              Display your content to users you choose to interact with (and
              your Circle, as applicable)
            </li>
            <li>Improve, troubleshoot, and secure the Service</li>
          </ul>
          <p>
            This license ends when your content is deleted from our systems,
            except where we must retain limited records for legal, security, or
            fraud-prevention reasons as described in the Privacy Policy.
          </p>

          <h5 className="mt-4 fw-bold">
            10) Paid Features, Subscriptions, and Billing (If Applicable)
          </h5>
          <p>
            Hivora Circle may offer paid features such as subscriptions,
            tokens/credits, or premium capabilities.
          </p>
          <p>If you purchase paid features, you agree:</p>
          <ul>
            <li>
              To pay all charges at the prices shown at the time of purchase
            </li>
            <li>
              That we may use third-party payment processors (e.g., Stripe,
              Apple In-App Purchase, Google Play Billing)
            </li>
            <li>
              That refunds are governed by the applicable store’s refund policy
              when purchases occur through Apple or Google
            </li>
            <li>
              That reversing/charging back payments may result in suspension or
              termination
            </li>
          </ul>

          <h5 className="mt-4 fw-bold">11) Account Termination and Deletion</h5>
          <h6>A) You May Delete Your Account</h6>
          <p>You may request account deletion through:</p>
          <ul>
            <li>In-app deletion settings, and/or</li>
            <li>
              <Link href="/delete-account" className="text-primary">
                {domain}/delete-account
              </Link>
            </li>
          </ul>
          <p>
            When your account is deleted, your profile will no longer be visible
            within Hivora Circle, subject to limited retention exceptions
            described in the Privacy Policy.
          </p>
          <h6>B) We May Suspend or Terminate Accounts</h6>
          <p>
            We may suspend or terminate your account at any time if we believe
            you:
          </p>
          <ul>
            <li>Violated these Terms</li>
            <li>Created risk or legal exposure</li>
            <li>Engaged in fraud, abuse, or unsafe behavior</li>
            <li>Harmed other users or the Service</li>
          </ul>
          <p>
            We may take these actions without notice where required for safety,
            trust, or legal compliance.
          </p>

          <h5 className="mt-4 fw-bold">12) Intellectual Property</h5>
          <p>
            Hivora Circle and all related trademarks, logos, designs, and software
            are owned by us or our licensors and are protected by intellectual
            property laws.
          </p>
          <p>
            You agree not to copy, modify, distribute, sell, lease, reverse
            engineer, or exploit any part of the Service except as explicitly
            permitted by law or by our written permission.
          </p>

          <h5 className="mt-4 fw-bold">13) Disclaimers</h5>
          <p>
            Hivora Circle is provided “AS IS” and “AS AVAILABLE,” without
            warranties of any kind, whether express or implied, including
            implied warranties of merchantability, fitness for a particular
            purpose, and non-infringement.
          </p>
          <p>We do not guarantee:</p>
          <ul>
            <li>
              That the Service will be uninterrupted, error-free, or secure at
              all times
            </li>
            <li>
              Any particular matchmaking results or compatibility outcomes
            </li>
            <li>The identity, conduct, intent, or safety of any user</li>
          </ul>
          <p>You use Hivora Circle at your own risk.</p>

          <h5 className="mt-4 fw-bold">14) Limitation of Liability</h5>
          <p>
            To the maximum extent permitted by law, Hivora Circle will not be
            liable for indirect, incidental, special, consequential, or punitive
            damages, or for loss of profits, data, goodwill, or other intangible
            losses.
          </p>
          <p>
            In all cases, Hivora’s total liability for any claim related to
            the Service will not exceed the greater of:
          </p>
          <ul>
            <li>
              The amount you paid to Hivora Circle in the 12 months before the
              event giving rise to the claim, or
            </li>
            <li>$100 USD</li>
          </ul>

          <h5 className="mt-4 fw-bold">15) Indemnification</h5>
          <p>
            You agree to defend, indemnify, and hold harmless Hivora Circle from
            and against claims, liabilities, damages, losses, and expenses
            (including reasonable legal fees) arising out of:
          </p>
          <ul>
            <li>Your use of the Service</li>
            <li>Your User Content</li>
            <li>Your violation of these Terms or applicable law</li>
            <li>Your interactions with other users</li>
          </ul>

          <h5 className="mt-4 fw-bold">16) Governing Law and Venue</h5>
          <p>
            These Terms are governed by the laws of the State of Maryland,
            without regard to conflict-of-law rules.
          </p>
          <p>
            Any dispute arising out of or relating to these Terms or the Service
            will be resolved exclusively in the state or federal courts located
            in Prince George’s County, Maryland, and you consent to jurisdiction
            and venue there.
          </p>

          <h5 className="mt-4 fw-bold">17) Changes to These Terms</h5>
          <p>
            We may update these Terms from time to time. When we do, we will
            update the “Last Updated” date above. Your continued use after
            changes become effective means you accept the updated Terms.
          </p>

          <h5 className="mt-4 fw-bold">18) Contact Us</h5>
          <p>
            Privacy Contact: support@hivora.ai
            <br />
            Support Contact: support@hivora.ai
            <br />
            Mail: 11808 Edmond Woods Way, Bowie, Maryland 20721, United States
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
